import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { from, Observable } from 'rxjs';
import { filter, map, mergeMap, tap, concatMap } from 'rxjs/operators';
import { PromptWithActionDialogComponent } from '../dialogs/prompt-with-action/prompt-with-action.component';

import { ImageData } from '../model/ImageData/ImageData';
import {
  MediaIcons,
  MediaTypeIcons,
  MediaTypes,
  SupportedAudioTypes,
  SupportedDocumentTypes,
  SupportedVideoTypes,
} from '../model/MediaTypes';
import { CommonService } from './common.service';
import { GuideAndBucketContentService } from './guide-and-bucket-content.service';

@Injectable({
  providedIn: 'root',
})
export class MediaContentService {
  constructor(
    private cService: CommonService,
    private gBucketService: GuideAndBucketContentService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  public getMedia(mediaEndpoint: string) {
    return this.cService.commonGet(mediaEndpoint).pipe(
      map((response: { data: Array<ImageData> }) => response.data),
      mergeMap((data: Array<ImageData>) => from(data)),
      concatMap((mediaData: ImageData) => {
        return this.getMediaStream(mediaData);
      })
    );
  }

  private getMediaStream(mediaData: ImageData) {
    return this.gBucketService.getDocument(mediaData.imageSrc).pipe(
      tap({
        error: () => {
          this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.requestedAssetNotAvailable);
        },
      }),
      filter((response) => response),
      mergeMap((response) => {
        return this.readAndAssignStream(mediaData, response);
      })
    );
  }

  private readAndAssignStream(mediaData: ImageData, streamResp): Observable<ImageData> {
    return new Observable((subscriber) => {
      const mediaStreamReader = new FileReader();

      mediaStreamReader.addEventListener(
        'load',
        () => {
          const newMediaData = {
            ...mediaData,
            imageStream: mediaStreamReader.result,
            downloadUrl: this.getDownloadUrl(mediaData.fileExtension, mediaStreamReader.result, mediaData.imageSrc),
            mediaType: this.getMediaType(mediaData.fileExtension.toLowerCase()),
            generatedFileName: this.generateFileName(mediaData),
            fileTypeIcons: this.getFileTypeIcons(mediaData.fileExtension.toLowerCase()),
          };

          subscriber.next(newMediaData);
          subscriber.complete();
        },
        false
      );

      mediaStreamReader.readAsDataURL(streamResp);
    });
  }

  private getDownloadUrl(
    fileExtension: string,
    streamReaderResult: string | ArrayBuffer,
    mediaSrc: string
  ): string | ArrayBuffer {
    let downloadUrl = streamReaderResult;

    if (fileExtension === '.pdf') {
      downloadUrl = 'data:application/pdf;base64,' + btoa(mediaSrc);
    }

    return downloadUrl;
  }

  private generateFileName(mediaData: ImageData): string {
    let fileName = mediaData.fileName;

    if (mediaData.firstName && mediaData.lastName && mediaData.date && mediaData.time) {
      fileName = `
                ${mediaData.firstName}
                ${mediaData.lastName} |
                ${mediaData.date}
                ${mediaData.time}
            `;
    }

    return fileName;
  }

  private getMediaType(fileExtension: string): string {
    let mediaType = MediaTypes.image;

    if (SupportedAudioTypes.includes(fileExtension) || SupportedAudioTypes.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.audio;
    }

    if (SupportedVideoTypes.includes(fileExtension) || SupportedVideoTypes.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.video;
    }

    if (SupportedDocumentTypes.includes(fileExtension) || SupportedDocumentTypes.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.document;
    }

    return mediaType;
  }

  private getFileTypeIcons(fileExtension: string): MediaIcons {
    let iconURL: MediaIcons = null;

    if (fileExtension === '.jpg') {
      iconURL = MediaTypeIcons.jpg;
    }

    if (SupportedVideoTypes.includes(fileExtension) || SupportedVideoTypes.includes(`.${fileExtension}`)) {
      iconURL = MediaTypeIcons.mov;
    }

    if (fileExtension === '.pdf') {
      iconURL = MediaTypeIcons.pdf;
    }

    if (fileExtension === '.png') {
      iconURL = MediaTypeIcons.png;
    }

    return iconURL;
  }

  private download(documentTitle: string, documentStream: string): void {
    let link = document.createElement('a');
    link.href = documentStream;
    link.download = documentTitle;
    link.click();
    link.remove();
  }

  public downloadBlobFromUrl(url: string, documentTitle: any, showNotification: boolean = false): void {
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (responseBlob: Blob) => {
        this.downloadBlob(documentTitle, responseBlob, true);
      },
    });
  }

  public downloadBlob(documentTitle: any, document: Blob, showNotification: boolean = false): void {
    this.download(documentTitle, window.URL.createObjectURL(document));

    if (showNotification) {
      this.showNotificationDialog(documentTitle);
    }
  }

  public downloadMedia(mediaData: ImageData, showNotification: boolean = false): void {
    this.download(mediaData.documentTitle, mediaData.imageStream);

    if (showNotification) {
      this.showNotificationDialog(mediaData.documentTitle);
    }
  }

  private showNotificationDialog(fileName: string): void {
    this.cService.promptConfig = {
      title: this.cService.readyTranslations.generic.vrmNotification,
      body: `${fileName}  ${this.cService.getSingleTranslation(
        'DER.fileDownloadedToYourDevice',
        'has been downloaded to your device.'
      )}`,
      actionButtonText: this.cService.readyTranslations.generic.ok,
    };

    this.dialog.open(PromptWithActionDialogComponent).afterClosed().subscribe();
  }
}
